import * as React from 'react'
import { cn } from '../../lib/utils'
import { KiArbeitet } from './KiArbeitet'

export interface DiktatFeldTexte {
  /** „Was wurde gemacht?" */
  titel: string
  /** „Tipp: Mikrofon der Tastatur" */
  tastatur: string
  /** „Vorschlag aus dem Text" */
  knopf: string
  /** „Die KI liest den Text" — ohne „…", die Punkte sind die Auslassungspunkte */
  arbeitet: string
  /** „Nur mit Netz — der Text bleibt stehen." */
  ohneNetz: string
  /** „Der Text geht zur Auswertung an die KI." */
  kiHinweis: string
}

export interface DiktatFeldProps {
  wert: string
  onChange: (text: string) => void
  /** Beim Verlassen — der Entwurf speichert den Text (ERP: feldweise, PWA: Ablage). */
  onBlur?: () => void
  /** Holt den Vorschlag; während das Versprechen läuft, steht `KiArbeitet` auf dem Knopf. */
  onVorschlag: () => Promise<void>
  online: boolean
  /** Feld und Knopf gesperrt (eingefroren, kein Recht) — der Text bleibt lesbar. */
  gesperrt?: boolean
  /** Meldung am Knopf — der Text bleibt. */
  fehler?: string | null
  texte: DiktatFeldTexte
  max?: number
  className?: string
}

/**
 * AP-46 21.7 — **Diktatfeld** für ERP und PWA: großes Textfeld mit Hinweis zur Tastatur, Zeichenzähler und
 * dem Knopf „Vorschlag aus dem Text" mit den Zuständen bereit · arbeitet · ohne Netz · gesperrt · Fehler.
 * Diktiert wird über das Mikrofon der Handytastatur — die App nimmt nur Text, keinen Ton.
 */
export function DiktatFeld({ wert, onChange, onBlur, onVorschlag, online, gesperrt, fehler, texte, max = 4000, className }: DiktatFeldProps) {
  const [laeuft, setLaeuft] = React.useState(false)
  const id = React.useId()
  const leer = !wert.trim()
  const zuLang = wert.length > max
  const holen = async () => {
    if (laeuft) return
    setLaeuft(true)
    try {
      await onVorschlag()
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <div className={cn('space-y-2', className)}>
      <label htmlFor={id} className="block text-sm font-medium">
        {texte.titel}
      </label>
      <textarea
        id={id}
        value={wert}
        disabled={gesperrt}
        rows={5}
        maxLength={max}
        placeholder={texte.tastatur}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70"
      />
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{texte.tastatur}</span>
        <span className={cn('tabular-nums', zuLang && 'text-destructive')}>
          {wert.length} / {max}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={gesperrt || laeuft || leer || zuLang || !online}
          onClick={() => void holen()}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {laeuft ? <KiArbeitet text={texte.arbeitet} /> : texte.knopf}
        </button>
        {!online && !gesperrt && <span className="text-sm text-muted-foreground">{texte.ohneNetz}</span>}
      </div>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <p className="text-xs text-muted-foreground">{texte.kiHinweis}</p>
    </div>
  )
}
